import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Note } from 'src/app/case/model/note';
import { NotesService } from 'src/app/core/service/notes.service';
import { NGXLogger } from 'ngx-logger';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { SettingService } from 'src/app/core/service/setting.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GroupDescriptor } from '@progress/kendo-data-query';
import { CaseService } from 'src/app/core/service/case.service';
import { MemberService } from 'src/app/core/service/member.service';

@Component({
  standalone: false,
  selector: 'app-notes-list-section',
  templateUrl: './notes-list-section.component.html',
  styleUrls: ['./notes-list-section.component.scss']
})
export class NotesListSectionComponent implements OnInit
{
  @Input() public memberId: number;
  @Input() public caseId: number;

  @Output() public refresh: EventEmitter<any> = new EventEmitter();

  public notes: any[] = [];
  public currentNote: Note = null;
  public notesView: GridViewManager;
  public notesCount: number;
  public pageSizes = [5, 10, 25, 50, 100];
  public enableAdvanced = false;      // Advanced mode not supported
  public notesDialogOpened = false;
  public isNew = false;

  private defaultNoteTitle: string;
  private showTaskNotesOnClientProfile = true;

  constructor(
    private notesService: NotesService,
    private logger: NGXLogger,
    private translate: TranslateService,
    private settingService: SettingService,
    private appConfigService: AppConfigService,
    private caseService: CaseService,
    private messageHandlerService: MessageHandlerService,
    private authenticationService: AuthenticationService,
    private memberService: MemberService) { }

  public ngOnInit(): void
  {
    this.getNotes(true);
  }

  /**
   * Creates a new Note for the Member.
   */
  public createNote(): void
  {
    const note = new Note();
    note.title = this.defaultNoteTitle;
    note.createdOn = new Date();
    const user = this.authenticationService.currentUser;
    note.author = { name: user.name, id: user.id };
    if (this.isCaseContext())
    {
      note.caseId = this.caseId;
    }
    else
    {
      note.memberId = this.memberId;
    }
    this.isNew = true;
    this.currentNote = note;
    this.notesDialogOpened = true;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.notesView.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.notesView.setGroup(groups);
  }

  /**
   * Event handler for removal of a Note.
   *
   * @param noteId Note ID
   */
  public onNoteRemoved(): void
  {
    this.getNotes(false);
    this.refresh.emit();
  }

  public onNoteAdded(): void
  {
    this.isNew = false;
    this.getNotes(false);
    this.refresh.emit();
  }

  public onNoteCancel(): void
  {
    this.isNew = false;
    this.currentNote = null;
    this.notesDialogOpened = false;
  }

  public editHandler(dataItem: Note): void
  {
    this.isNew = false;
    if (dataItem.taskId)
    {
      this.currentNote = dataItem;
      this.notesDialogOpened = true;
    }
    else
    {
      this.notesService.getNoteById(dataItem.id).subscribe({
        next: (note) =>
        {
          this.currentNote = note;
          this.notesDialogOpened = true;
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  public getTaskNoteBody(note: string): string
  {
    return note.replace('<p>', '<p class="ellipsis-text">');
  }

  private isCaseContext(): boolean
  {
    return (this.caseId !== undefined && this.caseId > 0);
  }

  private getNotes(initialization: boolean): void
  {
    this.notesDialogOpened = false;
    this.notesView = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.notesView.startLoading();
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.translate.get(['CASE', 'TASK', 'DEFAULT-NOTE-TITLE']).subscribe({
          next: (res) =>
          {
            this.defaultNoteTitle = res['DEFAULT-NOTE-TITLE'];
            this.showTaskNotesOnClientProfile = sessionSetting.showTaskNotesOnClientProfile;
            let serviceCall: any;
            if (this.isCaseContext())
            {
              serviceCall = this.notesService.getCaseNotes(this.caseId);
            }
            else
            {
              serviceCall = this.notesService.getMemberNotes(this.memberId);
            }
            serviceCall.subscribe({
              next: (data: any) =>
              {
                data.forEach((d: any) =>
                {
                  if (this.isCaseContext())
                  {
                    d.caseId = this.caseId;
                    d.type = res.CASE;
                  }
                  else
                  {
                    d.memberId = this.memberId;
                    d.type = sessionSetting.patientLabel;
                  }
                  d.taskId = null;
                  d.createdOn = new Date(d.createdOn);
                  d.lastModifiedOn = new Date(d.lastModifiedOn);
                  d.authorName = d.author ? d.author.fullName : '';
                });

                this.notesCount = data.length;

                // For case context, we must get tasks notes
                if (this.isCaseContext())
                {
                  this.caseService.getCaseDetailsTasksMinimal(this.caseId, 'ALL').subscribe({
                    next: (caseData) =>
                    {
                      this.notesCount += caseData.tasks.length;
                      caseData.tasks.forEach((d: any) =>
                      {
                        const taskNote = new Note();
                        taskNote.type = res.TASK;
                        taskNote.taskId = d.id;
                        taskNote.memberId = null;
                        taskNote.caseId = null;
                        taskNote.title = d.name;
                        taskNote.body = d.note;
                        taskNote.createdOn = new Date(d.createdOn);
                        taskNote.lastModifiedOn = new Date(d.lastModifiedOn);
                        taskNote.authorName = d.createdBy ? d.createdBy : '';
                        data.push(taskNote);
                      });

                      this.notesView.setData(data);
                      this.notesView.setSort([{ field: 'lastModifiedOn', dir: 'desc' }]);
                      this.notesView.stopLoading();
                      this.notes = data;
                      if (!initialization)
                      {
                        this.refresh.emit();
                      }
                    }
                  });
                }
                else
                {
                  // For member context: data is set to notes first; when showTaskNotesOnClientProfile,
                  if (this.showTaskNotesOnClientProfile)
                  {
                    // For member context, we must get tasks notes
                    this.memberService.getMemberTasks(this.memberId, 'ALL').subscribe({
                      next: (memberData) =>
                      {
                        this.notesCount += memberData.length;
                        memberData.forEach((d: any) =>
                        {
                          const taskNote = new Note();
                          taskNote.type = res.TASK;
                          taskNote.taskId = d.id;
                          taskNote.memberId = null;
                          taskNote.caseId = null;
                          taskNote.title = d.name;
                          taskNote.body = d.note;
                          taskNote.createdOn = new Date(d.createdOn);
                          taskNote.lastModifiedOn = new Date(d.lastModifiedOn);
                          taskNote.authorName = d.createdBy ? d.createdBy : '';
                          data.push(taskNote);
                        });

                        this.notesView.setData(data);
                        this.notesView.setSort([{ field: 'lastModifiedOn', dir: 'desc' }]);
                        this.notesView.stopLoading();
                        this.notes = data;
                        if (!initialization)
                        {
                          this.refresh.emit();
                        }
                      }
                    });
                  }
                  else
                  {
                    this.notesView.setData(data);
                    this.notesView.setSort([{ field: 'lastModifiedOn', dir: 'desc' }]);
                    this.notesView.stopLoading();
                    this.notes = data;
                    if (!initialization)
                    {
                      this.refresh.emit();
                    }
                  }
                }
              },
              error: (error: any) =>
              {
                this.logger.error(error);
                this.messageHandlerService.displayError(error);
              }
            });
          }
        });
      }
    });
  }
}
